import React from "react";
import Card from "../common/Card";
import EmptyState from "../common/EmptyState";
import LeadForm from "./LeadForm";

const DEFAULT_TRUST = [
  { icon: "⚡", text: "Most tradies reply within a few hours" },
  { icon: "🔒", text: "Your details only go to this tradie" },
  { icon: "✓", text: "No commitment — review the quote first" },
];

function TrustStrip() {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "10px",
        margin: "16px 0 18px 0",
      }}
    >
      {DEFAULT_TRUST.map((t) => (
        <div
          key={t.text}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 12px",
            background: "var(--surface-2)",
            border: "1px solid var(--hairline)",
            borderRadius: "10px",
            fontSize: "0.82rem",
            color: "var(--text-2, var(--text-soft))",
            lineHeight: 1.45,
          }}
        >
          <span style={{ fontSize: "1.05rem", flexShrink: 0 }} aria-hidden="true">
            {t.icon}
          </span>
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}

function BrandedHero({ business, config }) {
  const name = business.business_name || business.full_name || "Your tradie";
  const logoUrl = business.logo_url || null;

  const headline =
    (config?.intro_headline || "").trim() ||
    `Request a quote from ${name}`;
  const subhead =
    (config?.intro_subhead || "").trim() ||
    `Tell ${name} about your job — they'll reply with a personalised quote, usually within a few hours.`;

  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <Card style={{ marginBottom: "0.5rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={name}
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "14px",
              objectFit: "cover",
              border: "1px solid var(--hairline, rgba(140,100,80,0.18))",
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            aria-hidden="true"
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "14px",
              background: "var(--theme-primary-gradient, linear-gradient(135deg,#CC6E00 0%,#FF8A00 100%))",
              color: "#fff",
              fontWeight: 700,
              fontSize: "1.4rem",
              letterSpacing: "0.04em",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            style={{
              margin: "0 0 6px 0",
              fontSize: "1.4rem",
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            {headline}
          </h2>
          <p
            className="muted small"
            style={{ margin: 0, lineHeight: 1.5, fontSize: "0.88rem" }}
          >
            {subhead}
          </p>
        </div>
      </div>
    </Card>
  );
}

function DirectSubmitSuccess({ business, config, onSubmitAnother }) {
  const name = business?.business_name || business?.full_name || "the tradie";
  const customMessage = (config?.thank_you_message || "").trim();

  const steps = [
    { num: "1", title: "Request sent", body: `${name} has just been notified of your job.` },
    { num: "2", title: "They review the details", body: "Usually within a few hours during business hours." },
    { num: "3", title: "You get a quote", body: "Sent straight to your email — review, accept, or ask questions." },
  ];

  return (
    <div className="page customer-shell">
      <Card>
        <div style={{ textAlign: "center", padding: "28px 12px 12px" }}>
          <div
            aria-hidden="true"
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              background: "rgba(34, 197, 94, 0.15)",
              color: "#16a34a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 14px",
              fontSize: "1.8rem",
              fontWeight: 700,
            }}
          >
            ✓
          </div>
          <h2 style={{ margin: "0 0 6px", fontSize: "1.5rem", letterSpacing: "-0.01em" }}>
            Thanks — your request is in
          </h2>
          <p className="muted" style={{ maxWidth: "440px", margin: "0 auto", lineHeight: 1.55 }}>
            {customMessage ||
              `${name} will get back to you with a quote shortly. Check your email — it'll arrive there.`}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          {steps.map((s) => (
            <div
              key={s.num}
              style={{
                padding: "14px",
                background: "var(--surface-2)",
                border: "1px solid var(--hairline)",
                borderRadius: "12px",
              }}
            >
              <div
                style={{
                  fontSize: "0.7rem",
                  letterSpacing: "0.08em",
                  fontWeight: 700,
                  color: "var(--primary, #FF8A00)",
                  marginBottom: "4px",
                }}
              >
                STEP {s.num}
              </div>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>{s.title}</div>
              <div style={{ fontSize: "0.85rem", color: "var(--text-2, var(--text-soft))", lineHeight: 1.5 }}>
                {s.body}
              </div>
            </div>
          ))}
        </div>

        {onSubmitAnother && (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <button
              type="button"
              onClick={onSubmitAnother}
              style={{
                background: "transparent",
                border: "1px solid var(--hairline)",
                borderRadius: "10px",
                padding: "9px 18px",
                fontSize: "0.88rem",
                fontWeight: 600,
                color: "var(--text)",
                cursor: "pointer",
              }}
            >
              Submit another request
            </button>
          </div>
        )}
      </Card>
      <p
        className="muted small"
        style={{
          textAlign: "center",
          marginTop: "20px",
          fontSize: "0.75rem",
          letterSpacing: "0.02em",
        }}
      >
        Powered by{" "}
        <a
          href="https://remotequote.io"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          Remote Quote
        </a>
      </p>
    </div>
  );
}

export default function DirectQuotePage({
  business,
  loading,
  onSubmitRequest,
  session,
  role,
  profile,
  businessId,
}) {
  const [submitted, setSubmitted] = React.useState(false);
  const config = business?.direct_link_config || {};

  if (loading) {
    return (
      <div className="page customer-shell">
        <Card>
          <p className="muted">Loading business details…</p>
        </Card>
      </div>
    );
  }

  if (role === "tradie" && businessId && business && businessId === business.business_id) {
    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    return (
      <div className="page customer-shell">
        <Card>
          <h2>Your customer quote link</h2>
          <p className="muted">
            This is your customer quote link. Share it with your customers so
            they can request a quote directly from you.
          </p>
          {shareUrl && (
            <div
              style={{
                marginTop: "12px",
                padding: "10px 12px",
                background: "var(--surface-2, #FAFAFA)",
                border: "1px solid var(--hairline, rgba(140,100,80,0.18))",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <code
                style={{
                  fontSize: "0.85rem",
                  color: "var(--text)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "1 1 220px",
                  minWidth: 0,
                }}
              >
                {shareUrl}
              </code>
              <button
                type="button"
                onClick={() => {
                  try {
                    navigator.clipboard?.writeText(shareUrl);
                  } catch {}
                }}
                style={{
                  background: "var(--primary)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Copy link
              </button>
            </div>
          )}
        </Card>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="page customer-shell">
        <Card>
          <EmptyState
            icon="🔗"
            title="Link not found"
            description="This quote link is invalid, expired, or the business no longer exists. Contact the tradie who shared it for a fresh link."
          />
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <DirectSubmitSuccess
        business={business}
        config={config}
        onSubmitAnother={() => setSubmitted(false)}
      />
    );
  }

  return (
    <div className="page customer-shell">
      <BrandedHero business={business} config={config} />
      <TrustStrip />
      <LeadForm
        onSubmitRequest={async (request) => {
          const result = await onSubmitRequest(request);
          if (!result || result.ok !== false) setSubmitted(true);
          return result;
        }}
        prefillName={profile?.full_name || ""}
        prefillEmail={session?.user?.email || ""}
        tradieId={business?.business_id}
        directLinkConfig={config}
        restrictedTrades={Array.isArray(business?.trade_types) ? business.trade_types : null}
      />
      <p
        className="muted small"
        style={{
          textAlign: "center",
          marginTop: "20px",
          fontSize: "0.75rem",
          letterSpacing: "0.02em",
        }}
      >
        Powered by{" "}
        <a
          href="https://remotequote.io"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          Remote Quote
        </a>
      </p>
    </div>
  );
}
